import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Switch} from '@/components/commons/switch/Switch'
import {DetailsField} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {httpEditEvent, HttpEditEventOptions} from '@/features/agenda/http/agenda.http'
import {EventExtended} from '@/features/agenda/types'
import {usePermissions} from '@/features/auth/store/store'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {errorHandler} from '@/utilities/helpers'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect, useRef} from 'react'

interface AgendaEventDetailsRemoteSwitchContext {
    previousEvent?: EventExtended
}

export const AgendaEventDetailsRemoteSwitch = ({
    event,
    isTherapistNotAssignedToEvent
}: {
    event: EventExtended
    isTherapistNotAssignedToEvent: boolean
}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const hasToggledRef = useRef(false)

    // Not useEditEvent: refreshing the calendar behind the open dialog closes it. Only the extended
    // query is written here; the calendar is invalidated once the dialog closes (see below).
    const {mutate: updateIsRemote, isPending} = useMutation<
        unknown,
        AxiosError,
        HttpEditEventOptions,
        AgendaEventDetailsRemoteSwitchContext
    >({
        mutationFn: httpEditEvent,
        onMutate: async variables => {
            hasToggledRef.current = true
            const isRemote = !!(variables.payload as {isRemote?: boolean | null}).isRemote
            const queryKey = [QUERY_KEYS.AGENDA_EVENTS, 'extended', event.id]
            await queryClient.cancelQueries({queryKey})
            const previousEvent = queryClient.getQueryData<EventExtended>(queryKey)
            queryClient.setQueryData<EventExtended>(queryKey, old => (old ? {...old, isRemote} : old))
            return {previousEvent}
        },
        onError: (error, _variables, context) => {
            if (context?.previousEvent) {
                queryClient.setQueryData([QUERY_KEYS.AGENDA_EVENTS, 'extended', event.id], context.previousEvent)
            }
            errorHandler(error)
        }
    })

    // The dialog unmounts its content on close: refresh the calendar card then, the same way every
    // other event edit does. Guarded so StrictMode's mount/cleanup/mount cycle is a no-op.
    useEffect(
        () => () => {
            if (!hasToggledRef.current) return
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
        },
        []
    )

    const isDisabled =
        !permissions?.eventStatus.write ||
        isTherapistNotAssignedToEvent ||
        !!event.eventPatients?.some(ep => ep.project?.status === 'closed')

    return (
        <DetailsField>
            <Text size="textSm" palette="neutral" shade="500" weight="500">
                {t('accesses:remote')}
            </Text>
            <Flexbox gap={2} align="center">
                <Switch
                    size="sm"
                    checked={event.isRemote}
                    disabled={isDisabled || isPending}
                    onChange={checked =>
                        updateIsRemote({
                            urlParams: {eventId: event.id},
                            payload: {
                                isRemote: checked,
                                // The BE always requires these three even on a partial PATCH.
                                eventPatients: event.eventPatients?.map(ep => ({
                                    patientId: ep.patientId,
                                    projectId: ep.projectId
                                })),
                                eventConsultants: event.eventConsultants?.map(ec => ({
                                    userId: ec.userId,
                                    accessTypeId: ec.accessTypeId,
                                    substituteId: ec.substituteId
                                })),
                                eventTeamMembers: event.eventTeams?.map(et => ({
                                    treatmentRequestTeamId: et.treatmentRequestTeamId
                                }))
                            }
                        })
                    }
                />
                {event.isRemote && event.remoteUrl && (
                    <ButtonLink to={event.remoteUrl} target="_blank" shape="rectangle" size="xs" variant="secondary">
                        {t('commons:link')}
                    </ButtonLink>
                )}
            </Flexbox>
        </DetailsField>
    )
}
